import moment from 'moment';
import { useSettingsStore } from '~/stores/settings';
function getStartOfDayOffset() {
    const settingsStore = useSettingsStore();
    return settingsStore.startOfDay;
}
function normalize_date(dateParam) {
    return new Date(dateParam.getTime());
}
export function seconds_to_duration(seconds) {
    // Returns a human-readable duration string
    const hrs = Math.floor(seconds / 60 / 60);
    const min = Math.floor((seconds / 60) % 60);
    const sec = Math.floor(seconds % 60);
    const l = [];
    if (hrs > 0) {
        l.push(hrs + 'h');
        l.push(min + 'm');
    }
    else if (min > 0) {
        l.push(min + 'm');
    }
    l.push(sec + 's');
    return l.join(' ');
}
export function friendlydate(timestamp) {
    const now = moment();
    const m = moment.parseZone(timestamp);
    const sinceNow = moment.duration(m.diff(now));
    if (-sinceNow.asSeconds() <= 60) {
        return `${Math.round(-sinceNow.asSeconds())}s ago`;
    }
    else if (-sinceNow.asSeconds() <= 60 * 60 * 24) {
        return sinceNow.humanize(true);
    }
    return sinceNow.humanize(true);
}
export function get_day_start_with_offset(dateParam, offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    const dateMoment = dateParam ? moment(dateParam) : moment().startOf('day');
    const start_of_day_hours = parseInt(offset.split(':')[0]);
    const start_of_day_minutes = parseInt(offset.split(':')[1]);
    return dateMoment.hour(start_of_day_hours).minute(start_of_day_minutes).format();
}
/** A period's [start, end) in unix seconds: from `start` (a day start, as
 * get_day_start_with_offset gives) to the start of day on the date
 * `length` later. The end is found on its own date, never by adding to the
 * start's clock time: on the morning the clocks go forward a 02:00 start is
 * 03:00 (02:00 doesn't exist), and adding a day to that would end the
 * period an hour into the next day. */
export function periodUnixRange(start, span, offset) {
    const startDate = moment(start).format('YYYY-MM-DD');
    const endDate = moment(start)
        .add(span[0], span[1])
        .format('YYYY-MM-DD');
    return [dayStartInstant(startDate, offset), dayStartInstant(endDate, offset)];
}
/** The first moment of a day on this browser's wall clock, given the start
 * of day: the moment the clock first reads that time or later on that date.
 * Usually the start of day itself; on the morning the clocks go forward,
 * a start inside the missing hour (02:30) begins the day when the clock
 * jumps (03:00), where setting the hour would give 03:30. */
export function dayStartInstant(date, offset) {
    const hours = get_hour_offset(offset);
    const startMinute = Math.round(hours * 60);
    const onOrAfter = (unix) => {
        const m = moment.unix(unix);
        const d = m.format('YYYY-MM-DD');
        return d > date || (d === date && m.hours() * 60 + m.minutes() >= startMinute);
    };
    let t = moment(get_day_start_with_offset(date, offset)).unix();
    // Step back while the minute before still reads as this day's start or later.
    for (let i = 0; i < 180 && onOrAfter(t - 60); i++)
        t -= 60;
    return t;
}
// Return the startOfDay offset as a number of hours
export function get_hour_offset(offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    const start_of_day_hours = parseInt(offset.split(':')[0]);
    const start_of_day_minutes = parseInt(offset.split(':')[1]);
    return start_of_day_hours + start_of_day_minutes / 60;
}
export function get_day_end_with_offset(date, offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    return moment(get_day_start_with_offset(date, offset)).add(1, 'days').format();
}
export function get_day_period(date, offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    return get_day_start_with_offset(date, offset) + '/' + get_day_end_with_offset(date, offset);
}
export function get_prev_day(datestr) {
    return moment(datestr).add(-1, 'days');
}
export function get_next_day(datestr) {
    return moment(datestr).add(1, 'days');
}
export function get_offset_duration(offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    const [hours, minutes] = offset.split(':');
    return moment.duration({ hours: Number(hours), minutes: Number(minutes) });
}
export function get_today_with_offset(offset) {
    if (!offset) {
        offset = getStartOfDayOffset();
    }
    // Today on the wall clock: the date, or the day before while the clock
    // reads earlier than the start of day (never by subtracting hours from
    // the moment, which goes wrong the mornings the clocks change).
    const now = moment();
    const date = now.format('YYYY-MM-DD');
    return now.unix() >= dayStartInstant(date, offset)
        ? date
        : now.clone().subtract(1, 'day').format('YYYY-MM-DD');
}
export function format_weekday_short(dateParam, locale) {
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(normalize_date(dateParam));
}
export function format_day_of_month(dateParam, locale) {
    return new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(normalize_date(dateParam));
}
export function get_short_month_labels(locale) {
    return Array.from({ length: 12 }, (_, month) => new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2020, month, 1, 12)));
}
// Format a date as locale-aware short date (e.g. "Jun 15" in en-US, "15 jun" in sv-SE)
export function format_date_short(date, locale) {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d);
}
// Format a date with weekday prefix (e.g. "Mon, Jun 15" in en-US, "mån 15 jun" in sv-SE)
export function format_date_with_weekday(date, locale) {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(d);
}
// Format a time-of-day string locale-aware (e.g. "14:30:05" or "2:30:05 PM" per browser locale)
export function format_time_of_day(date, locale) {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(d);
}
